import { SignedIn, SignedOut, useCurrentUser, useSessionTokenGetter } from '@develio/auth/client';
import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { analytics } from '../lib/posthog';
import { ErrorBoundary } from './ErrorBoundary';
import { PageviewTracker } from './PageviewTracker';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
const ONBOARDING_DISMISSED_KEY = 'develio:onboarding-dismissed';

/**
 * Protected app shell. Gates the entire authenticated surface: if the user is
 * not signed in, redirects to /sign-in. If signed in, renders the sidebar +
 * topbar + child route.
 *
 * This replaces the Sprint 1 "hello world" single-page shell.
 */
function AnalyticsIdentifier() {
  const user = useCurrentUser();
  useEffect(() => {
    if (user?.clerkUserId) {
      analytics.identify(user.clerkUserId, { email: user.email });
    }
  }, [user?.clerkUserId, user?.email]);
  return null;
}

/**
 * First-login redirect: pings onboarding-status once after sign-in; if the
 * org has never run the setup form (default jurisdiction still shows the
 * "Sample City" placeholder), pushes the user to `/onboarding`. The redirect
 * is one-shot — we set a localStorage flag so a user who clicks "Skip for
 * now" or navigates away isn't trapped. Already-on-/onboarding users are
 * left alone.
 */
function OnboardingRedirect() {
  const getToken = useSessionTokenGetter();
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked) return;
    if (location.pathname === '/onboarding') return;
    // User explicitly skipped — don't keep redirecting them.
    if (typeof window !== 'undefined' && window.localStorage.getItem(ONBOARDING_DISMISSED_KEY)) {
      setChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as { needsInitialSetup?: boolean };
        if (!cancelled && data.needsInitialSetup) {
          navigate('/onboarding', { replace: true });
        }
      } catch {
        // non-fatal — let the user land on whatever page they came in on
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checked, location.pathname, getToken, navigate]);

  return null;
}

/**
 * Subscription gate (card-required reverse trial): every authenticated app
 * route is walled until the org has a live subscription. We ping /me once; if
 * it reports needsSubscription, the owner is sent to /start-trial to enter a
 * card before any access. While the check is in flight we render nothing to
 * avoid a flash of app chrome the user may not be entitled to.
 */
function SubscriptionGate({ children }: { children: ReactNode }) {
  const getToken = useSessionTokenGetter();
  const [needsSubscription, setNeedsSubscription] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          // Fail open: don't trap a user out of the app on a transient error.
          if (!cancelled) setNeedsSubscription(false);
          return;
        }
        const data = (await res.json()) as { needsSubscription?: boolean };
        if (!cancelled) setNeedsSubscription(!!data.needsSubscription);
      } catch {
        if (!cancelled) setNeedsSubscription(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  if (needsSubscription === null) return null;
  if (needsSubscription) return <Navigate to="/start-trial" replace />;
  return <>{children}</>;
}

export function ProtectedShell() {
  return (
    <>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
      <SignedIn>
        <SubscriptionGate>
          <PageviewTracker />
          <AnalyticsIdentifier />
          <OnboardingRedirect />
          <div className="flex h-screen bg-gradient-to-br from-slate-50 via-white to-brand-teal-50/40 text-slate-900">
            <Sidebar />
            <div className="flex flex-1 flex-col overflow-hidden">
              <TopBar />
              <main className="flex-1 overflow-y-auto px-10 py-8">
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </main>
            </div>
          </div>
        </SubscriptionGate>
      </SignedIn>
    </>
  );
}
